"use client";

import { Moon, Sun } from "lucide-react";
import { useTheme } from "./providers/theme-provider";

export function ThemeToggle({ className }: { className?: string }) {
    const { setTheme, theme } = useTheme();

    return (
        <button
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            className={`
                group relative flex items-center justify-center
                w-11 h-11 rounded-full
                bg-gradient-to-br from-slate-100 to-slate-200
                dark:from-slate-800 dark:to-slate-900
                border-2 border-transparent
                shadow-lg shadow-amber-200/30 dark:shadow-indigo-500/20
                hover:shadow-xl hover:shadow-amber-300/40 dark:hover:shadow-indigo-400/30
                hover:scale-110 active:scale-95
                transition-all duration-300 ease-out
                before:absolute before:inset-0 before:rounded-full
                before:bg-gradient-to-br before:from-amber-400 before:via-orange-400 before:to-yellow-500
                dark:before:from-indigo-500 dark:before:via-purple-500 dark:before:to-blue-500
                before:p-[2px] before:-z-10 before:opacity-80
                hover:before:opacity-100
                overflow-hidden
                ${className}
            `}
            title="Toggle theme"
            aria-label="Toggle theme"
        >
            {/* Background glow effect */}
            <span className="
                absolute inset-0 rounded-full
                bg-gradient-to-br from-amber-100 to-orange-50
                dark:from-indigo-950 dark:to-slate-900
                transition-all duration-300
            " />

            {/* Sun Icon */}
            <Sun className="
                absolute h-5 w-5
                text-amber-500 dark:text-amber-300
                rotate-0 scale-100 opacity-100
                dark:-rotate-180 dark:scale-0 dark:opacity-0
                transition-all duration-500 ease-out
                drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]
                group-hover:drop-shadow-[0_0_12px_rgba(251,191,36,0.7)]
            " />

            {/* Moon Icon */}
            <Moon className="
                absolute h-5 w-5
                text-indigo-400 dark:text-indigo-300
                rotate-180 scale-0 opacity-0
                dark:rotate-0 dark:scale-100 dark:opacity-100
                transition-all duration-500 ease-out
                dark:drop-shadow-[0_0_8px_rgba(129,140,248,0.6)]
                dark:group-hover:drop-shadow-[0_0_12px_rgba(129,140,248,0.8)]
            " />

            {/* Decorative stars for dark mode */}
            <span className="
                absolute top-1.5 right-2 w-1 h-1 rounded-full
                bg-transparent dark:bg-white/80
                scale-0 dark:scale-100
                transition-all duration-500 delay-200
            " />
            <span className="
                absolute bottom-2.5 left-2 w-0.5 h-0.5 rounded-full
                bg-transparent dark:bg-white/60
                scale-0 dark:scale-100
                transition-all duration-500 delay-300
            " />

            <span className="sr-only">Toggle theme</span>
        </button>
    );
}
